import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { motionOn, finePointer } from '@/lib/motion';

const Arrow = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
    <path d="M4 12 12 4M5 4h7v7" />
  </svg>
);

// Lucide icons passed as children are replaced by the arrow-swap disc.
const isIcon = (c) => React.isValidElement(c) && typeof c.type !== 'string' && c.props && 'size' in c.props;

/**
 * Pill button with a magnetic pull toward the cursor (fine pointers only).
 * Renders <Link> if `to` is provided, <a> if `href`, else <button>.
 */
export function MagneticButton({
  children,
  href,
  to,
  variant = 'primary', // primary | ghost | outline
  size,
  className = '',
  strength = 0.3,
  external = false,
  icon,
  ...rest
}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!finePointer() || !motionOn()) return undefined;
    const xTo = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'power3' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'power3' });
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      xTo((e.clientX - r.left - r.width / 2) * strength);
      yTo((e.clientY - r.top - r.height / 2) * (strength + 0.1));
    };
    const onLeave = () => gsap.to(el, { x: 0, y: 0, duration: 0.9, ease: 'elastic.out(1, 0.4)' });
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      gsap.killTweensOf(el);
    };
  }, [strength]);

  const kids = React.Children.toArray(children);
  const hadIcon = kids.some(isIcon);
  const label = kids.filter((c) => !isIcon(c));
  const showIcon = icon ?? (hadIcon || variant === 'primary');

  const cls = [
    'nd-btn',
    `nd-btn--${variant === 'outline' ? 'outline' : variant === 'ghost' ? 'ghost' : 'primary'}`,
    size === 'sm' ? 'nd-btn--sm' : '',
    showIcon ? '' : 'nd-btn--noicon',
    className,
  ].join(' ');

  const inner = (
    <>
      <span>{label}</span>
      {showIcon && (
        <span className="nd-btn__ico" aria-hidden>
          <Arrow />
          <Arrow />
        </span>
      )}
    </>
  );

  if (to) {
    return <Link ref={ref} to={to} className={cls} {...rest}>{inner}</Link>;
  }
  if (href) {
    return (
      <a
        ref={ref}
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className={cls}
        {...rest}
      >
        {inner}
      </a>
    );
  }
  return <button ref={ref} type="button" className={cls} {...rest}>{inner}</button>;
}
